import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import GoogleMapReact from 'google-map-react';
import createAddress from 'api/address/createAddress';
import { createCamera, getAddressesByCustomer } from 'api/canvms';

const CAMERA_PRESETS = {
  Generic: { protocol: 'rtsp', type: 'h264', port: '554', path: '/stream1' },
  Hikvision: { protocol: 'rtsp', type: 'h264', port: '554', path: '/Streaming/Channels/101' },
  Dahua: { protocol: 'rtsp', type: 'h264', port: '554', path: '/cam/realmonitor?channel=1&subtype=0' },
  Axis: { protocol: 'rtsp', type: 'h264', port: '554', path: '/axis-media/media.amp' },
  Amcrest: { protocol: 'rtsp', type: 'h264', port: '554', path: '/cam/realmonitor?channel=1&subtype=0' },
};

const DEFAULT_CENTER = { lat: 51.05, lng: -114.07 };

function MapMarker() {
  return (
    <LocationOnIcon
      sx={{
        color: 'red',
        fontSize: 36,
        transform: 'translate(-50%, -100%)',
      }}
    />
  );
}

function AddCameraDialog({ open, onClose, siteId, onCameraAdded, customers }) {
  // Form state - Camera Connection
  const [name, setName] = useState('');
  const [preset, setPreset] = useState('Generic');
  const [protocol, setProtocol] = useState('rtsp');
  const [streamType, setStreamType] = useState('h264');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('554');
  const [path, setPath] = useState('/stream1');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Form state - Location
  const [customerId, setCustomerId] = useState('');
  const [addressTab, setAddressTab] = useState(0); // 0 = existing, 1 = new
  const [addressId, setAddressId] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);

  // New address form fields
  const [addrTitle, setAddrTitle] = useState('');
  const [addrLine1, setAddrLine1] = useState('');
  const [addrLine2, setAddrLine2] = useState('');
  const [addrCity, setAddrCity] = useState('');
  const [addrProvince, setAddrProvince] = useState('');
  const [addrPostalCode, setAddrPostalCode] = useState('');
  const [addrCountry, setAddrCountry] = useState('CA');

  // Map state
  const [markerPosition, setMarkerPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(12);
  const [googleMapsReady, setGoogleMapsReady] = useState(false);
  const mapRef = useRef(null);
  const mapsRef = useRef(null);

  // Address search state
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName('');
      setPreset('Generic');
      setProtocol('rtsp');
      setStreamType('h264');
      setHost('');
      setPort('554');
      setPath('/stream1');
      setUsername('');
      setPassword('');
      setShowPassword(false);
      setCustomerId('');
      setAddressTab(0);
      setAddressId('');
      setAddresses([]);
      setAddrTitle('');
      setAddrLine1('');
      setAddrLine2('');
      setAddrCity('');
      setAddrProvince('');
      setAddrPostalCode('');
      setAddrCountry('CA');
      setMarkerPosition(null);
      setMapCenter(DEFAULT_CENTER);
      setMapZoom(12);
      setSearchInput('');
      setSearchResults([]);
      setError(null);
    }
  }, [open]);

  // Apply preset defaults
  const handlePresetChange = useCallback((newPreset) => {
    setPreset(newPreset);
    const defaults = CAMERA_PRESETS[newPreset];
    if (defaults) {
      setProtocol(defaults.protocol);
      setStreamType(defaults.type);
      setPort(defaults.port);
      setPath(defaults.path);
    }
  }, []);

  // Load addresses when customer changes
  useEffect(() => {
    if (!customerId) {
      setAddresses([]);
      setAddressId('');
      return;
    }

    let cancelled = false;
    const fetchAddresses = async () => {
      try {
        setAddressesLoading(true);
        const response = await getAddressesByCustomer(customerId);
        if (!cancelled) {
          setAddresses(response.data?.addresses || []);
        }
      } catch (err) {
        console.error('[CANVMS/AddCameraDialog] Error fetching addresses:', err);
        if (!cancelled) {
          setAddresses([]);
        }
      } finally {
        if (!cancelled) {
          setAddressesLoading(false);
        }
      }
    };

    fetchAddresses();
    setAddressId('');

    return () => {
      cancelled = true;
    };
  }, [customerId]);

  // Search for addresses using Google Places
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value || value.length < 3 || !autocompleteServiceRef.current) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    searchTimeoutRef.current = setTimeout(() => {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: value,
          componentRestrictions: { country: ['ca', 'us'] },
          types: ['geocode', 'establishment'],
        },
        (results) => {
          setSearchResults(results || []);
          setSearchLoading(false);
        }
      );
    }, 300);
  }, []);

  // When user selects a place from search results, get full details
  const handleSelectPlace = useCallback((prediction) => {
    if (!placesServiceRef.current) return;

    setSearchInput(prediction.description);
    setSearchResults([]);

    placesServiceRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['address_components', 'geometry', 'formatted_address'],
      },
      (place, status) => {
        if (status !== 'OK' || !place) return;

        // Parse address components
        let street = '';
        let city = '';
        let province = '';
        let postalCode = '';
        let country = '';

        for (const comp of (place.address_components || [])) {
          const type = comp.types[0];
          if (type === 'street_number') street = comp.short_name;
          if (type === 'route') street = street ? `${street} ${comp.short_name}` : comp.short_name;
          if (type === 'locality') city = comp.short_name;
          if (type === 'administrative_area_level_1') province = comp.short_name;
          if (type === 'country') country = comp.short_name;
          if (type === 'postal_code') postalCode = comp.short_name;
        }

        setAddrLine1(street);
        setAddrCity(city);
        setAddrProvince(province);
        setAddrPostalCode(postalCode);
        setAddrCountry(country || 'CA');
        setAddrTitle((prev) => prev || place.formatted_address || street);

        // Move map to location
        if (place.geometry?.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          setMarkerPosition({ lat, lng });
          setMapCenter({ lat, lng });
          setMapZoom(16);
        }
      }
    );
  }, []);

  const handleMapClick = useCallback(({ lat, lng }) => {
    setMarkerPosition({ lat, lng });
  }, []);

  const handleGoogleApiLoaded = useCallback(({ map, maps }) => {
    mapRef.current = map;
    mapsRef.current = maps;
    autocompleteServiceRef.current = new maps.places.AutocompleteService();
    placesServiceRef.current = new maps.places.PlacesService(map);
    setGoogleMapsReady(true);
  }, []);

  const handleSubmit = async () => {
    if (!name.trim() || !host.trim() || !port.trim() || !path.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      let finalAddressId = addressId || null;

      // If on "New Address" tab and fields are filled, create the address first
      if (addressTab === 1 && addrLine1.trim() && customerId) {
        try {
          const addrResult = await createAddress({
            Title: addrTitle.trim() || addrLine1.trim(),
            Line1: addrLine1.trim(),
            Line2: addrLine2.trim() || null,
            City: addrCity.trim(),
            Province: addrProvince.trim().toUpperCase(),
            PostalCode: addrPostalCode.trim().toUpperCase(),
            Country: addrCountry.trim(),
            CustomerId: customerId,
            TypeId: 1,
            PatrolType: 'Static',
          });
          finalAddressId = addrResult?.Data?.AddressId || addrResult?.AddressId || null;
        } catch (addrErr) {
          console.error('[CANVMS/AddCameraDialog] Error creating address:', addrErr);
          setError('Failed to create address. Please check the address fields.');
          setSubmitting(false);
          return;
        }
      }

      const cameraData = {
        name: name.trim(),
        type: streamType,
        protocol,
        host: host.trim(),
        port: port.trim(),
        path: path.trim(),
      };

      if (username.trim()) {
        cameraData.username = username.trim();
      }
      if (password) {
        cameraData.password = password;
      }
      if (markerPosition) {
        cameraData.latitude = markerPosition.lat;
        cameraData.longitude = markerPosition.lng;
      }
      if (customerId) {
        cameraData.customer_id = customerId;
      }
      if (finalAddressId) {
        cameraData.address_id = finalAddressId;
      }

      await createCamera(siteId, cameraData);
      onCameraAdded();
    } catch (err) {
      console.error('[CANVMS/AddCameraDialog] Error creating camera:', err);
      setError(
        err.response?.data?.error?.message ||
          err.response?.data?.message ||
          err.message ||
          'Failed to add camera'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Camera</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Section 1 - Camera Connection */}
          <Typography variant="subtitle1" fontWeight="bold">
            Camera Connection
          </Typography>

          <TextField
            label="Camera Name"
            required
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <FormControl fullWidth>
            <InputLabel>Camera Preset</InputLabel>
            <Select
              value={preset}
              label="Camera Preset"
              onChange={(e) => handlePresetChange(e.target.value)}
            >
              {Object.keys(CAMERA_PRESETS).map((key) => (
                <MenuItem key={key} value={key}>
                  {key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Protocol</InputLabel>
              <Select
                value={protocol}
                label="Protocol"
                onChange={(e) => setProtocol(e.target.value)}
              >
                <MenuItem value="rtsp">RTSP</MenuItem>
                <MenuItem value="http">HTTP</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Stream Type</InputLabel>
              <Select
                value={streamType}
                label="Stream Type"
                onChange={(e) => setStreamType(e.target.value)}
              >
                <MenuItem value="h264">H.264</MenuItem>
                <MenuItem value="mjpeg">MJPEG</MenuItem>
                <MenuItem value="jpeg">JPEG</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="IP Address / Hostname"
              required
              fullWidth
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="192.168.1.100"
            />
            <TextField
              label="Port"
              required
              value={port}
              onChange={(e) => setPort(e.target.value)}
              type="number"
              sx={{ width: 120, flexShrink: 0 }}
            />
          </Box>

          <TextField
            label="Stream Path"
            required
            fullWidth
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="/stream1"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Username"
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
              label="Password"
              fullWidth
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Section 2 - Location */}
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle1" fontWeight="bold">
            Location
          </Typography>

          <FormControl fullWidth>
            <InputLabel>Customer</InputLabel>
            <Select
              value={customerId}
              label="Customer"
              onChange={(e) => setCustomerId(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {(customers || []).map((c) => (
                <MenuItem key={c.cust_id} value={c.cust_id}>
                  {c.cust_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary">
            Click on the map to set the camera location, or search an address below
          </Typography>

          <Box sx={{ height: 300, width: '100%', borderRadius: 1, overflow: 'hidden' }}>
            <GoogleMapReact
              bootstrapURLKeys={{ key: import.meta.env.VITE_GOOGLE_API_KEY, libraries: ['places'] }}
              center={mapCenter}
              zoom={mapZoom}
              onClick={handleMapClick}
              onGoogleApiLoaded={handleGoogleApiLoaded}
              yesIWantToUseGoogleMapApiInternals
            >
              {markerPosition && (
                <MapMarker lat={markerPosition.lat} lng={markerPosition.lng} />
              )}
            </GoogleMapReact>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Latitude"
              fullWidth
              value={markerPosition ? markerPosition.lat.toFixed(6) : ''}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="Longitude"
              fullWidth
              value={markerPosition ? markerPosition.lng.toFixed(6) : ''}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Box>

          <Divider />

          <Tabs
            value={addressTab}
            onChange={(_, v) => setAddressTab(v)}
            sx={{ minHeight: 36 }}
          >
            <Tab label="Existing Address" sx={{ minHeight: 36, py: 0 }} />
            <Tab label="New Address" sx={{ minHeight: 36, py: 0 }} />
          </Tabs>

          {addressTab === 0 && (
            <FormControl fullWidth disabled={!customerId || addressesLoading}>
              <InputLabel>Address</InputLabel>
              <Select
                value={addressId}
                label="Address"
                onChange={(e) => setAddressId(e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {addresses.map((addr) => (
                  <MenuItem key={addr.address_id} value={addr.address_id}>
                    {addr.title || [addr.line1, addr.city, addr.province].filter(Boolean).join(', ') || `Address ${addr.address_id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {addressTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  label="Search Address"
                  fullWidth
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  disabled={!googleMapsReady}
                  placeholder={googleMapsReady ? 'Start typing an address...' : 'Loading map...'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {searchLoading ? <CircularProgress size={18} /> : <SearchIcon color="action" />}
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                />
                {searchResults.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1300,
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      maxHeight: 200,
                      overflow: 'auto',
                      boxShadow: 3,
                    }}
                  >
                    {searchResults.map((result) => (
                      <Box
                        key={result.place_id}
                        onClick={() => handleSelectPlace(result)}
                        sx={{
                          px: 2,
                          py: 1,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2">
                            {result.structured_formatting.main_text}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {result.structured_formatting.secondary_text}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <TextField
                label="Address Title"
                fullWidth
                value={addrTitle}
                onChange={(e) => setAddrTitle(e.target.value)}
                placeholder="e.g. Main Entrance Camera"
                size="small"
              />

              <TextField
                label="Address Line 1"
                fullWidth
                value={addrLine1}
                onChange={(e) => setAddrLine1(e.target.value)}
                size="small"
              />

              <TextField
                label="Address Line 2"
                fullWidth
                value={addrLine2}
                onChange={(e) => setAddrLine2(e.target.value)}
                size="small"
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="City"
                  fullWidth
                  value={addrCity}
                  onChange={(e) => setAddrCity(e.target.value)}
                  size="small"
                />
                <TextField
                  label="Province"
                  value={addrProvince}
                  onChange={(e) => setAddrProvince(e.target.value)}
                  sx={{ width: 100, flexShrink: 0 }}
                  inputProps={{ maxLength: 2 }}
                  size="small"
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Postal Code"
                  value={addrPostalCode}
                  onChange={(e) => setAddrPostalCode(e.target.value)}
                  sx={{ width: 140, flexShrink: 0 }}
                  size="small"
                />
                <TextField
                  label="Country"
                  value={addrCountry}
                  onChange={(e) => setAddrCountry(e.target.value)}
                  sx={{ width: 100, flexShrink: 0 }}
                  inputProps={{ maxLength: 2 }}
                  size="small"
                />
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting} variant="outlined">
          Cancel
        </Button>
        <Tooltip title="Pending VMS migration">
          <span>
            <Button
              variant="contained"
              disabled
            >
              Add Camera
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}

export default AddCameraDialog;
